
import { useTranslation } from 'react-i18next'
import { Input } from '../ui-kitNew/controlsNew'
import { Select } from '../ui-kitNew/selectNew'
import { MultiSelect } from '../ui-kitNew/multi-selectNew'
import { resolveField } from '../../lib/reportNew/fieldsNew'
import { operatorMeta } from '../../lib/reportNew/filtersNew'

/**
 * Control de valor que se adapta al operador elegido.
 * Es la pieza que hace que el filtrado sea "dinámico": no hay ningún
 * formulario escrito a mano por campo, todo se deriva de los metadatos.
 */
export function ValueInput({ condition, onChange, options = [], fields }) {
  const { t } = useTranslation()
  const meta = operatorMeta(condition.field, condition.operator, fields)
  const field = resolveField(condition.field, fields)
  if (!meta) return null

  const set = (patch) => onChange({ ...condition, ...patch })

  switch (meta.input) {
    case 'none':
      return null

    case 'multi':
      return (
        <MultiSelect
          options={options}
          value={Array.isArray(condition.value) ? condition.value : []}
          onChange={(value) => set({ value })}
          placeholder={t('ui.filters.chooseValues')}
        />
      )

    case 'select':
      return (
        <Select
          size="sm"
          searchable={options.length > 8}
          options={options}
          value={condition.value}
          onChange={(value) => set({ value })}
          placeholder={t('ui.filters.chooseValue')}
          ariaLabel={t('ui.filters.valueOf', { field: field?.label })}
        />
      )

    case 'number':
      return (
        <Input
          size="sm"
          type="number"
          inputMode="decimal"
          value={condition.value}
          onChange={(event) => set({ value: event.target.value })}
          placeholder={field?.unit ? t('ui.filters.valueIn', { unit: field.unit }) : t('ui.filters.value')}
          aria-label={t('ui.filters.valueOf', { field: field?.label })}
        />
      )

    case 'numberRange':
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          <div className="min-w-[4rem] flex-1">
            <Input
              size="sm"
              type="number"
              value={condition.value}
              onChange={(event) => set({ value: event.target.value })}
              placeholder={t('ui.filters.min')}
              aria-label={t('ui.filters.minValue')}
            />
          </div>
          <span className="text-[12px] text-muted-foreground">{t('ui.filters.and')}</span>
          <div className="min-w-[4rem] flex-1">
            <Input
              size="sm"
              type="number"
              value={condition.value2}
              onChange={(event) => set({ value2: event.target.value })}
              placeholder={t('ui.filters.max')}
              aria-label={t('ui.filters.maxValue')}
            />
          </div>
        </div>
      )

    case 'date':
      return (
        <Input
          size="sm"
          type="date"
          value={condition.value}
          onChange={(event) => set({ value: event.target.value })}
          aria-label={t('ui.filters.dateOf', { field: field?.label })}
        />
      )

    case 'dateRange':
      return (
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-[10.5rem] flex-1">
            <Input
              size="sm"
              type="date"
              value={condition.value}
              onChange={(event) => set({ value: event.target.value })}
              aria-label={t('ui.filters.dateFrom')}
            />
          </div>
          <span className="text-[12px] text-muted-foreground">{t('ui.filters.and')}</span>
          <div className="min-w-[10.5rem] flex-1">
            <Input
              size="sm"
              type="date"
              value={condition.value2}
              onChange={(event) => set({ value2: event.target.value })}
              aria-label={t('ui.filters.dateTo')}
            />
          </div>
        </div>
      )

    case 'text':
    default:
      return (
        <Input
          size="sm"
          value={condition.value}
          onChange={(event) => set({ value: event.target.value })}
          placeholder={t('ui.filters.typeText')}
          aria-label={t('ui.filters.valueOf', { field: field?.label })}
        />
      )
  }
}
